/**
 * 商品 API 服務
 * 使用 RTK Query 封裝商品相關的 API 調用
 * 
 * 注意：目前使用 mockBaseQuery 進行開發測試
 * 待 API 整合後，將 mockBaseQuery 替換為 baseQuery
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from '@/services/baseApi';
import { IApiResponse, IPaginatedResponse } from '@/types/api';
import {
  ProductBase,
  IProductListParams,
  IProductFormData,
  IBatchUpdateProductsRequest,
  IBatchUpdateProductsResponse,
  IBatchDeleteProductsRequest,
} from '@/types/product';
import { ConstantUtil } from '@/utils/ConstantUtil';

/**
 * 商品 API 服務
 */
export const productApi = createApi({
  reducerPath: 'productApi',
  baseQuery: baseQueryWithErrorHandling,
  // baseQuery, // 真實 API baseQuery（待 API 整合後使用）
  tagTypes: ['Product'],
  endpoints: (builder) => ({
    // 獲取商品列表
    getProducts: builder.query<IApiResponse<IPaginatedResponse<ProductBase>>, IProductListParams>({
      query: (params) => ({
        url: ConstantUtil.API_PRODUCTS,
        method: 'GET',
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? ConstantUtil.PAGE_SIZE,
          ...(params.search && { search: params.search }),
          ...(params.searchKeyword && { searchKeyword: params.searchKeyword }),
          ...(params.status && params.status !== 'all' && { status: params.status }),
          ...(params.category && params.category !== 'all' && { category: params.category }),
          ...(params.brand && params.brand !== 'all' && { brand: params.brand }),
          ...(params.contract && params.contract !== 'all' && { contract: params.contract }),
          ...(params.region && params.region !== 'all' && { region: params.region }),
          ...(params.minPrice !== undefined && { minPrice: params.minPrice }),
          ...(params.maxPrice !== undefined && { maxPrice: params.maxPrice }),
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
        },
      }),
      providesTags: ['Product'],
    }),

    // 獲取商品詳情
    getProductById: builder.query<IApiResponse<ProductBase>, string>({
      query: (id) => ({
        url: ConstantUtil.API_PRODUCT_BY_ID(id),
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'Product', id }],
    }),

    // 創建商品
    createProduct: builder.mutation<IApiResponse<ProductBase>, IProductFormData>({
      query: (data) => ({
        url: ConstantUtil.API_PRODUCT_CREATE,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Product'],
    }),

    // 更新商品
    updateProduct: builder.mutation<IApiResponse<ProductBase>, { id: string; data: Partial<IProductFormData> }>({
      query: ({ id, data }) => ({
        url: ConstantUtil.API_PRODUCT_UPDATE(id),
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Product', id }, 'Product'],
    }),

    // 刪除商品（軟刪除）
    deleteProduct: builder.mutation<IApiResponse<void>, string>({
      query: (id) => ({
        url: ConstantUtil.API_PRODUCT_DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['Product'],
    }),

    // 批量更新商品
    batchUpdateProducts: builder.mutation<IApiResponse<IBatchUpdateProductsResponse>, IBatchUpdateProductsRequest>({
      query: (data) => ({
        url: ConstantUtil.API_PRODUCT_BATCH_UPDATE,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Product'],
    }),

    // 批量刪除商品
    batchDeleteProducts: builder.mutation<IApiResponse<{ success: number; failed: number }>, IBatchDeleteProductsRequest>({
      query: (data) => ({
        url: ConstantUtil.API_PRODUCT_BATCH_DELETE,
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: ['Product'],
    }),

    // 上傳商品圖片
    uploadProductImage: builder.mutation<IApiResponse<{ image_url: string }>, { id: string; file: File }>({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: ConstantUtil.API_PRODUCT_UPLOAD_IMAGE(id),
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Product', id }],
    }),

    // 更新商品狀態
    updateProductStatus: builder.mutation<IApiResponse<ProductBase>, { id: string; status: 'on_sale' | 'off_sale' | 'draft' | 'deleted' }>({
      query: ({ id, status }) => ({
        url: ConstantUtil.API_PRODUCT_UPDATE(id),
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Product', id }, 'Product'],
    }),

    // 批量更新商品狀態
    batchUpdateProductStatus: builder.mutation<IApiResponse<IBatchUpdateProductsResponse>, { ids: string[]; status: 'on_sale' | 'off_sale' | 'draft' | 'deleted' }>({
      query: ({ ids, status }) => ({
        url: ConstantUtil.API_PRODUCT_BATCH_UPDATE,
        method: 'PUT',
        body: { ids, updates: { status } },
      }),
      invalidatesTags: ['Product'],
    }),
  }),
});

// 導出自動生成的 hooks
export const {
  useGetProductsQuery,
  useGetProductByIdQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useBatchUpdateProductsMutation,
  useBatchDeleteProductsMutation,
  useUploadProductImageMutation,
  useUpdateProductStatusMutation,
  useBatchUpdateProductStatusMutation,
} = productApi;
